import { GENERATION_LOCK_STALE_MS } from '../../../../functions/src/utils/generationWindow';
import { studyDateFormatFromDocs } from '../../../../functions/src/studyProfile/studyDateFormat';
import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { query, where, onSnapshot, getDocs, getDoc, doc } from 'firebase/firestore';
import { adoptButtonState } from '../../features/dataManagement/adoptButtonState';
import { summarizeDictionarySize } from '../../features/dataManagement/dictionarySizeSummary';
import { dictionaryChangedSince, dictionaryChangedNotice } from '../../features/dataManagement/dictionaryChangedNotice';
import { httpsCallable } from '../../lib/callable';
import { functions, db } from '../../firebase';
import { CLOUD_FUNCTIONS, CALLABLE_TIMEOUTS } from '../../constants/firebase';
import { downloadBlockedReason } from '../../features/finalGate/downloadCheck';
import DownloadButton from '../../features/downloads/DownloadButton';
import { useDocumentDownload } from '../../features/downloads/useDocumentDownload';
import {
  studyDocumentDownloadOptions,
  DOWNLOAD_DOC_NAMES,
  type DownloadCardId,
  type DownloadOption,
  type DownloadRequest,
} from '../../features/downloads/studyDocumentDownloads';
import { consentDownloadRequest, selectConsentArtifact } from '../../features/downloads/consentDownload';
import {
  projectDoc,
  artifactsCollection,
  dataManagementDoc,
  crfInstructionGuideChunksCollection,
  crfPackageVisitMappingDoc,
  crfGuideJobPointerDoc,
  guideJobDoc,
} from '../../constants/firestorePaths';
import {
  deriveConsentBadge,
  deriveVisitMatrixBadge,
  type ConsentArtifactLike,
  type VisitMappingLike,
  type StudyDocBadge,
} from './studyDocStatus';
import { crfReadiness, crfReadinessMessage, crfPartialMessage } from '../../features/crf/crfReadiness';
import { needsApprovedForms } from '../../features/crf/approvedFormPrerequisite';
import { approvedFormsGate } from '../../features/crf/approvedFormsGate';
import { isApprovedCrfStatus, sessionFormCountOf } from '../../features/crf/crfApprovalRule';
import { hasUsableRendering } from './crfFormDownloads';
import ConsistencyChip from '../../components/verification/ConsistencyChip';
import RenameNotice from '../../features/renames/RenameNotice';
import { useOpenRenameNotices } from '../../features/renames/useOpenRenameNotices';
import { documentKeyOf } from '../../features/renames/renameNoticeText';
import ProseDocChip from '../../components/verification/ProseDocChip';
import GenerationCheckBadge from '../../components/verification/GenerationCheckBadge';
import { GENERATION_CHECK_DOCS } from '../../features/verification/generationCheck';

// The six prose documents, card id → pipeline doc key (inc6 step E: their
// cards graduated from the static "not yet covered" label to a live
// per-document verification chip — available-with-warning, never a block).
const PROSE_DOC_KEY_BY_CARD: Record<string, string> = {
  dataManagementPlan: 'dmp',
  edts: 'edts',
  medicalCodingSpec: 'medicalCodingSpec',
  drcp: 'drcp',
  saerp: 'saerp',
  dblc: 'dblc',
};

// Cards built from the data dictionary (design 2026-09-14 §5); not crfInstructionGuide, which does not read the dictionary.
const DICTIONARY_DEPENDENT_DOCS: readonly DocumentId[] = [
  'dataDictionary',
  'editChecks',
  'sdtmMapping',
  'edts',
  'medicalCodingSpec',
  'drcp',
  'saerp',
  'dblc',
  'dataManagementPlan',
];

// Where each card's document is stored, for the rename notice (plan step 2,
// features/renames). Every card that prints field names — the guide included:
// its notice offers Regenerate only, since it is stored as a finished file.
const RENAME_NOTICE_DOC_PATH: Partial<Record<DocumentId, string>> = {
  dataDictionary: 'dataManagement/dataDictionary',
  crfInstructionGuide: 'dataManagement/crfInstructionGuide',
  dataManagementPlan: 'dataManagement/dmp',
  editChecks: 'dataManagement/editCheckSpec',
  sdtmMapping: 'dataManagement/sdtmMapping',
  edts: 'dataManagement/edts',
  medicalCodingSpec: 'dataManagement/medicalCodingSpec',
  drcp: 'dataManagement/drcp',
  saerp: 'dataManagement/saerp',
  dblc: 'dataManagement/dblc',
};
import { useToast } from '../../contexts/ToastContext';
import { usePermission } from '../../components/permissions/PermissionTooltip';
import {
  Table,
  CheckSquare,
  Loader2,
  RefreshCw,
  FileSpreadsheet,
  BookOpen,
  Eye,
  EyeOff,
  X,
  AlertCircle,
  CheckCircle2,
  Play,
  GitMerge,
  FileSignature,
  Calendar,
  ArrowLeftRight,
  SearchCheck,
  Hash,
  GitCompareArrows,
  Lock,
  ExternalLink,
  ChevronDown,
  Upload,
  FileUp,
} from 'lucide-react';
import ApplyDictionaryEditsModal from '../../components/ApplyDictionaryEditsModal';
import DMPPanel from '../../components/dmp/DMPPanel';
import EditCheckSpecPanel from '../../components/editChecks/EditCheckSpecPanel';
import SDTMMappingPanel from '../../components/sdtm/SDTMMappingPanel';
import EDTSPanel from '../../components/edts/EDTSPanel';
import MCSPanel from '../../components/mcs/MCSPanel';
import DRCPPanel from '../../components/studyDocuments/DRCPPanel';
import SAERPPanel from '../../components/studyDocuments/SAERPPanel';
import DBLCPanel from '../../components/studyDocuments/DBLCPanel';
import { hubBadge } from '../../features/documents/documentReadiness';
import { freshnessNoticeFor, freshnessRefreshKey } from '../../features/documents/documentFreshness';
import { useDocumentFreshness } from '../../features/documents/useDocumentFreshness';

type DocumentId = 'dataDictionary' | 'crfInstructionGuide' | 'dataManagementPlan' | 'editChecks' | 'sdtmMapping' | 'edts' | 'medicalCodingSpec' | 'drcp' | 'saerp' | 'dblc';
type DocumentStatus = 'not_started' | 'generating' | 'generated';

/**
 * Download filename for the CRF Instruction Guide:
 * `<StudyShortCode>_CRF_Completion_Guidelines_YYYY-MM-DD.docx`
 * (e.g. FOG_CRF_Completion_Guidelines_2026-06-14). Non-alphanumeric runs in the
 * study code collapse to "_"; an empty/unknown code falls back to "Study".
 */
const guideDownloadFileName = (studyShort?: string | null): string => {
  const clean = (studyShort || 'Study').trim().replace(/[^A-Za-z0-9]+/g, '_').replace(/^_+|_+$/g, '') || 'Study';
  return `${clean}_CRF_Completion_Guidelines_${new Date().toISOString().slice(0, 10)}.docx`;
};

/**
 * The Download button on a card's face. `group` is the card id, which the
 * card's View panel also uses, so a download running in either place
 * disables both and cannot be started twice.
 */
function CardDownloadButton({ projectId, group, options, className }: {
  projectId: string | undefined;
  group: string;
  options: DownloadOption[];
  className?: string;
}) {
  const download = useDocumentDownload(projectId, group);
  return <DownloadButton download={download} options={options} className={className} />;
}

interface DocumentCardConfig {
  id: DocumentId;
  title: string;
  description: string;
  icon: typeof Table;
  iconBgColor: string;
  iconColor: string;
  comingSoon?: boolean;
}

interface EditCheck {
  id: string;
  field: string;
  checkType: string;
  severity: string;
  rule: string;
  technicalRule: string;
  protocolReference: string;
}

const DOCUMENT_CARDS: DocumentCardConfig[] = [
  {
    id: 'dataDictionary',
    title: 'Data Dictionary',
    description: 'CDASH-compliant data dictionary generated from your CRF forms.',
    icon: Table,
    iconBgColor: 'bg-blue-100',
    iconColor: 'text-blue-600',
  },
  {
    id: 'crfInstructionGuide',
    title: 'CRF Instruction Guide',
    description: 'Field-by-field completion instructions for site staff to ensure consistent data entry.',
    icon: BookOpen,
    iconBgColor: 'bg-teal-100',
    iconColor: 'text-teal-600',
  },
  {
    id: 'dataManagementPlan',
    title: 'Data Management Plan',
    description: 'Comprehensive DMP covering data collection, validation, coding, and regulatory compliance.',
    icon: FileSpreadsheet,
    iconBgColor: 'bg-amber-100',
    iconColor: 'text-amber-600',
  },
  {
    id: 'editChecks',
    title: 'Edit Checks',
    description: 'Programmatic validation rules for range, consistency, completeness, and logical checks.',
    icon: CheckSquare,
    iconBgColor: 'bg-green-100',
    iconColor: 'text-green-600',
  },
  {
    id: 'sdtmMapping',
    title: 'SDTM Mapping Specification',
    description: 'Variable-level mapping from CRF fields to CDISC SDTM domains, with controlled terminology, derivations, and Word download. Backfills your data dictionary with regulator-grade SDTM assignments.',
    icon: GitMerge,
    iconBgColor: 'bg-purple-100',
    iconColor: 'text-purple-600',
  },
  {
    id: 'edts',
    title: 'External Data Transfer Specification',
    description: 'Defines how data flows from external vendors (labs, IRT, ePRO, imaging, safety) into the clinical database. Per-source variable metadata, transfer schedule, blinding, reconciliation, and sponsor/vendor signature blocks.',
    icon: ArrowLeftRight,
    iconBgColor: 'bg-orange-100',
    iconColor: 'text-orange-600',
  },
  {
    id: 'medicalCodingSpec',
    title: 'Medical Coding Specification',
    description: 'MedDRA, WHODrug, and LOINC coding conventions, dictionary versions, per-domain coding procedures, and quality control workflows.',
    icon: Hash,
    iconBgColor: 'bg-lime-100',
    iconColor: 'text-lime-600',
  },
  {
    id: 'drcp',
    title: 'Data Review and Cleaning Plan',
    description: 'Risk-based plan for ongoing data review, query lifecycle, external reconciliation, KRIs/QTLs, cleaning milestones, and database lock readiness criteria. Grounded in ICH E6(R3) and SCDM GCDMP.',
    icon: SearchCheck,
    iconBgColor: 'bg-violet-100',
    iconColor: 'text-violet-600',
  },
  {
    id: 'saerp',
    title: 'SAE Reconciliation Procedure',
    description: 'Procedure for reconciling serious adverse events between the EDC and the safety database. Includes data point catalog, discrepancy tier framework, blinding rules, milestone schedule, and DBL sign-off criteria. Grounded in ICH E2A/E2B(R3)/E6(R3), 21 CFR 312.32, and CDASH SAE Supplement v2.0.',
    icon: GitCompareArrows,
    iconBgColor: 'bg-rose-100',
    iconColor: 'text-rose-600',
  },
  {
    id: 'dblc',
    title: 'Database Lock Checklist',
    description: 'Study-tailored 19-category lock readiness checklist covering query closure, coding completion, SAE recon, external data recon, deviation review, audit trail, signatures, and post-lock activities. Conditional items resolved against your study scope. Grounded in SCDM GCDMP, ICH E6(R3), 21 CFR Part 11, and CDISC submission standards.',
    icon: Lock,
    iconBgColor: 'bg-slate-100',
    iconColor: 'text-slate-700',
  },
];

// ============================================================
// Study Documents — non-inline cards (linkouts and placeholders)
// ============================================================
// The 5 inline cards (DMP, Data Dict, CRF Guide, Edit Checks, SDTM Mapping)
// stay in DOCUMENT_CARDS above and keep their full functionality.
// These extra cards either link out to canonical pages elsewhere or are
// placeholders for documents we haven't built yet.

interface StudyDocumentExtra {
  id: string;
  name: string;
  shortName: string;
  description: string;
  icon: React.ReactNode;
  iconBgColor: string;   // Tailwind bg-{color}-100 utility — matches inline card pattern
  iconColor: string;     // Tailwind text-{color}-600 utility — matches inline card pattern
  /**
   * Static product-capability marker — only meaningful for `planned` (genuinely
   * not-built-yet) cards, which render a "Coming Soon" badge. Cards whose status
   * reflects the user's study set `dynamicSource` instead and derive their badge
   * at runtime; for those, `status` is omitted.
   */
  status?: 'planned';
  /**
   * When set, the card's badge is derived per-study from the real underlying
   * artifact (overrides any static `status`).
   */
  dynamicSource?: 'consent' | 'visitMatrix';
  behavior: 'linkout' | 'placeholder';
  group: 'design_setup' | 'data_management' | 'study_conduct' | 'study_closeout';
  route?: string;
  /** Query string for the link-out, without the leading '?'. */
  query?: string;
  routeLabel?: string;
  tooltip?: string;
  /** Which shared download the card offers (features/downloads), if any. */
  download?: 'visitFormMatrix' | 'consentForm';
}

const STUDY_DOCUMENTS_EXTRA: StudyDocumentExtra[] = [
  // ─── Study Design & Setup ────────────────────────────────────────
  {
    id: 'icf',
    name: 'Informed Consent Form',
    shortName: 'ICF',
    description: 'Informed consent document covering purpose, procedures, risks, benefits, confidentiality, privacy and data protection for each study country, and signatures.',
    icon: <FileSignature className="h-5 w-5" />,
    iconBgColor: 'bg-indigo-100',
    iconColor: 'text-indigo-600',
    dynamicSource: 'consent',
    behavior: 'linkout',
    group: 'design_setup',
    route: 'study-design',
    routeLabel: 'in Study Design',
    download: 'consentForm',
  },
  {
    id: 'visit_form_matrix',
    name: 'Visit & Form Matrix',
    shortName: 'Visit Matrix',
    description: 'Schedule of which forms are collected at which visits, including required vs. optional and visit windows. Download as Word, CSV or ODM-XML.',
    icon: <Calendar className="h-5 w-5" />,
    iconBgColor: 'bg-yellow-100',
    iconColor: 'text-yellow-600',
    dynamicSource: 'visitMatrix',
    behavior: 'linkout',
    group: 'design_setup',
    route: 'crf-builder',
    query: 'view=schedule',
    routeLabel: 'Form Assignments',
    tooltip: 'Download the saved visit/form matrix as Word, CSV or ODM-XML.',
    download: 'visitFormMatrix',
  },
  // Case Report Forms and Annotated CRFs are deliberately NOT cards here. Both
  // are produced and downloaded in the CRF Builder (the "CRF Packets" card on
  // its Forms view), and a hub card that only bounced you there earned its
  // space back. This hub is for documents it can hand you directly.

];

const STUDY_GROUP_LABELS: Record<StudyDocumentExtra['group'], string> = {
  design_setup: 'Study Design & Setup',
  data_management: 'Data Management Setup',
  study_conduct: 'Study Conduct',
  study_closeout: 'Study Closeout',
};

const STUDY_GROUP_ORDER: Array<StudyDocumentExtra['group']> = [
  'design_setup',
  'data_management',
  'study_conduct',
  'study_closeout',
];

// Map each existing inline card from DOCUMENT_CARDS to its group on the
// Study Documents layout. CRF Instruction Guide goes into Study Design &
// Setup; the rest go into Data Management Setup. Test/UAT documents have
// moved to the Testing & Qualification group on the Study Build page.
const INLINE_CARD_GROUPS: Record<DocumentId, StudyDocumentExtra['group']> = {
  dataDictionary: 'data_management',
  crfInstructionGuide: 'design_setup',
  dataManagementPlan: 'data_management',
  editChecks: 'data_management',
  sdtmMapping: 'data_management',
  edts: 'data_management',
  medicalCodingSpec: 'study_conduct',
  drcp: 'study_conduct',
  saerp: 'study_conduct',
  dblc: 'study_closeout',
};

// Coerce a Firestore timestamp-ish value (Timestamp, {seconds}, number of
// ms, or ISO string) to epoch millis. Used to compare a job's updatedAt
// against Date.now() for stalled detection. Returns 0 when unknown, which
// makes the comparison treat the job as "just seen" until a real value
// arrives (a server write always carries a real timestamp).
function timestampToMillis(value: any): number {
  if (!value) return 0;
  if (typeof value.toMillis === 'function') return value.toMillis();
  if (typeof value.seconds === 'number') return value.seconds * 1000;
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const ms = Date.parse(value);
    return Number.isNaN(ms) ? 0 : ms;
  }
  return 0;
}

// Normalize a precondition payload's form list to display strings. The
// background-job path returns duplicateForms as an array of
// { formCode, formName } objects, while the sync path returns a plain
// string[]; handle both defensively. Prefers a "CODE — Name" label when an
// object carries both, otherwise the code (or the bare string).
function formLabels(forms: unknown): string[] {
  if (!Array.isArray(forms)) return [];
  return forms
    .map((f: any) => {
      if (typeof f === 'string') return f;
      if (f && typeof f === 'object') {
        if (f.formCode && f.formName) return `${f.formCode} — ${f.formName}`;
        return String(f.formCode || f.formName || '');
      }
      return '';
    })
    .filter((s) => s.length > 0);
}

// 15 minutes — how long an active job's updatedAt may go stale before the
// card surfaces a "may be stalled" note with a Restart button.
const GUIDE_JOB_STALL_MS = 15 * 60 * 1000;

export default function DataManagementHub() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  // Privilege gate — Viewers / Reviewers see disabled regenerate buttons
  // and a "Not yet generated" hint instead of the Generate CTA.
  const canGenerate = usePermission('generate');
  // Which cards carry a rename notice: their older dictionary-changed line points to it.
  const openRenameNotices = useOpenRenameNotices(projectId);
  const [loading, setLoading] = useState(true);
  // The study's chosen EDC (studySetup.edcSystem). The REDCap-format download is
  // only offered when this is 'redcap'.
  const [edcSystem, setEdcSystem] = useState<string | undefined>(undefined);
  const [projectName, setProjectName] = useState('');
  const [plan, setPlan] = useState<any>(null);
  const [dmpDoc, setDmpDoc] = useState<any>(null); // Comprehensive DMP document
  const [editCheckSpec, setEditCheckSpec] = useState<any>(null); // Edit check specification
  const [dictData, setDictData] = useState<any>(null);
  const [guideData, setGuideData] = useState<any>(null);
  // False until the crfInstructionGuide doc listener has resolved at least once.
  // Gates the card's actions so it shows a neutral "Loading…" state instead of
  // flashing the "Generate" buttons before the (possibly chunked) guide loads.
  const [guideLoaded, setGuideLoaded] = useState(false);
  const [sdtmDoc, setSdtmDoc] = useState<any>(null);
  const [edtsDoc, setEdtsDoc] = useState<any>(null);
  const [mcsDoc, setMcsDoc] = useState<any>(null);
  const [drcpDoc, setDrcpDoc] = useState<any>(null);
  const [saerpDoc, setSaerpDoc] = useState<any>(null);
  const [dblcDoc, setDblcDoc] = useState<any>(null);
  // dataManagement/dictionaryAdoption: when the study last adopted a dictionary (null if never).
  const [adoption, setAdoption] = useState<any>(null);
  // Freshness notices for the six documents, fetched again when any is regenerated.
  const freshnessNotices = useDocumentFreshness(projectId, freshnessRefreshKey({
    dataManagementPlan: timestampToMillis(dmpDoc?.generatedAt),
    edts: timestampToMillis(edtsDoc?.generatedAt),
    medicalCodingSpec: timestampToMillis(mcsDoc?.generatedAt),
    drcp: timestampToMillis(drcpDoc?.generatedAt),
    saerp: timestampToMillis(saerpDoc?.generatedAt),
    dblc: timestampToMillis(dblcDoc?.generatedAt),
  }));
  // The latest adoption's history record, read only when dictionaryAdoption
  // predates its stored counts (no lastCompleted), for the dictionary card.
  const [adoptionHistory, setAdoptionHistory] = useState<any>(null);
  // Per-study status for the link-out cards (ICF, Visit & Form Matrix).
  const [consentArtifact, setConsentArtifact] = useState<ConsentArtifactLike | null>(null);
  const [visitMapping, setVisitMapping] = useState<VisitMappingLike | null>(null);
  const { showToast } = useToast();
  const [expandedDocId, setExpandedDocId] = useState<DocumentId | null>(null);
  const [documentStatuses, setDocumentStatuses] = useState<Record<DocumentId, DocumentStatus>>({
    dataDictionary: 'not_started',
    crfInstructionGuide: 'not_started',
    dataManagementPlan: 'not_started',
    editChecks: 'not_started',
    sdtmMapping: 'not_started',
    edts: 'not_started',
    medicalCodingSpec: 'not_started',
    drcp: 'not_started',
    saerp: 'not_started',
    dblc: 'not_started',
  });
  // Track which documents are currently generating
  const [generatingDocs, setGeneratingDocs] = useState<Set<DocumentId>>(new Set());
  // CRF stats for gating CRF-derived artifacts.
  //   total           — any CRF artifact exists (blocks when 0)
  //   approved        — approved/final forms (what generators include)
  //   unrenderedForms — names of approved forms with no PDF rendering the
  //                     CRF Instruction Guide can embed (hasUsableRendering;
  //                     a Word-only upload does not count — the guide's
  //                     rasterizer is PDF-only). These forms must be rendered
  //                     (Generate CRF / Generate All CRFs in the CRF Builder)
  //                     before the guide can include them. Names (not just a
  //                     count) so the warning can tell the user exactly which
  //                     forms to render.
  const [crfStats, setCrfStats] = useState<{ total: number; approved: number; unrenderedForms: string[] } | null>(null);
  // Forms in the CRF BUILDER's working session — a different store from the crf
  // artifacts above, which are written by APPROVAL. Without this the hub cannot
  // tell "no forms exist" from "forms exist but none approved", and on
  // 2026-08-28 it told a customer their 38 forms "have not been generated".
  const [sessionFormCount, setSessionFormCount] = useState<number | null>(null);
  const crfReady = crfReadiness({
    sessionFormCount,
    approvedArtifactCount: crfStats === null ? null : crfStats.approved,
  });
  // Downstream generation needs at least ONE approved form (the server builds
  // from the approved subset and refuses only when none is), so the hub gates on
  // `canGenerate`, not on `ready`, which means EVERY form approved and stays the
  // CRF Builder's rule. Cards in APPROVED_FORM_PREREQUISITE_DOCS all use it.
  const hasApprovedForms = crfReady.canGenerate;
  // Last CRF Instruction Guide generation problem, pinned to the guide card so
  // it survives a user looking away (the toast is persistent but dismissible).
  // `message` already embeds the reference code for unexpected failures and the
  // "what to do" guidance for the specific/structured cases; `forms` names the
  // forms to fix when a precondition error identifies them. Cleared on success.
  const [guideError, setGuideError] = useState<{ message: string; forms?: string[]; errorRef?: string } | null>(null);

  // ── CRF Instruction Guide background job ──────────────────────────
  // Live status of the most recent background guide job (status/progress
  // doc at guideJobs/{jobId}). `null` when no job is being watched. Drives
  // the real progress bar ("Writing form instructions — 12 of 50"), the success
  // toast on completion, the error surfacing on failure, and the
  // stalled-detection note. The pointer doc (dataManagement/crfGuideJob)
  // tells us WHICH job to watch; this holds that job's contents.
  const [guideJob, setGuideJob] = useState<
    | {
        jobId: string;
        status: 'rendering' | 'finalizing' | 'done' | 'error' | 'superseded';
        formsTotal?: number;
        formsDone?: number;
        formsFailed?: number;
        quickMode?: boolean;
        errorRef?: string;
        errorMessage?: string;
        updatedAtMs: number;
        createdAtMs: number;
      }
    | null
  >(null);
  // 1-second ticker driving the live elapsed-time display on an active job.
  // Sourced from the job's createdAt (server truth), so it survives a refresh.
  const [jobNowMs, setJobNowMs] = useState(() => Date.now());
  // Set true by the stalled-detection interval when an active job's
  // updatedAt hasn't advanced for ~15 min. Shown as a "may be stalled"
  // note with a Restart button. Reset whenever the job doc updates.
  const [guideJobStalled, setGuideJobStalled] = useState(false);
  // The jobId we last fired a success toast for, so a re-render of the
  // "done" job doc doesn't re-toast.
  const guideJobDoneRef = useRef<string | null>(null);

  // Generation progress tracking
  const [generationProgress, setGenerationProgress] = useState<{
    docId: DocumentId | null;
    step: number;
    message: string;
    percent: number;
    startedAt: number;
  }>({ docId: null, step: 0, message: '', percent: 0, startedAt: 0 });
  const progressTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const elapsedTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Check if CRF artifacts exist
  // Watch the project's chosen EDC so the REDCap download shows only for REDCap studies.
  useEffect(() => {
    if (!projectId) return;
    const unsub = onSnapshot(
      projectDoc(projectId),
      (snap) => {
        setEdcSystem(((snap.data() as any)?.studySetup || {}).edcSystem);
        // Names the consent download, as the Study Design page does.
        setProjectName((snap.data() as any)?.name || '');
      },
      () => {},
    );
    return () => unsub();
  }, [projectId]);

  // The CRF Builder's working session, so the hub can name the true state.
  useEffect(() => {
    if (!projectId) return;
    const ref = doc(db, 'projects', projectId, 'crfBuilder', 'session');
    const unsub = onSnapshot(
      ref,
      (snap) => {
        setSessionFormCount(sessionFormCountOf(snap.data() as { forms?: unknown } | undefined));
      },
      // A read failure must not read as "no forms": leave it unknown.
      () => setSessionFormCount(null),
    );
    return () => unsub();
  }, [projectId]);

  useEffect(() => {
    if (!projectId) return;

    const q = query(
      artifactsCollection(projectId),
      where('type', '==', 'crf')
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      let approved = 0;
      const unrenderedForms: string[] = [];
      snapshot.forEach((d) => {
        const data = d.data();
        if (isApprovedCrfStatus(data.status)) {
          approved++;
          if (!hasUsableRendering({ sourceFilePath: data.sourceFilePath, sourcePacketPath: data.sourcePacketPath })) {
            unrenderedForms.push(String(data.name || d.id));
          }
        }
      });
      unrenderedForms.sort((a, b) => a.localeCompare(b));
      setCrfStats({ total: snapshot.size, approved, unrenderedForms });
    });

    return () => unsubscribe();
  }, [projectId]);

  // Listen for the consent artifact (drives the Informed Consent Form card's
  // per-study badge). Mirrors ConsentBuilder: any artifact of type
  // 'consent_form'. The untouched seed reads as "Not started"; see
  // deriveConsentBadge for the threshold.
  useEffect(() => {
    if (!projectId) return;
    const q = query(artifactsCollection(projectId), where('type', '==', 'consent_form'));
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        // The same rule as Study Design (selectConsentArtifact): the last match.
        setConsentArtifact(selectConsentArtifact(snapshot.docs.map((d) => d.data())) as ConsentArtifactLike | null);
      },
      (error) => console.error('Error loading consent artifact:', error),
    );
    return () => unsubscribe();
  }, [projectId]);

  // Listen for the visit/form mapping (drives the Visit & Form Matrix card's
  // per-study badge). "Ready" only once a real matrix with at least one
  // assignment exists; see deriveVisitMatrixBadge.
  useEffect(() => {
    if (!projectId) return;
    const unsubscribe = onSnapshot(
      crfPackageVisitMappingDoc(projectId),
      (snapshot) => {
        setVisitMapping(snapshot.exists() ? (snapshot.data() as VisitMappingLike) : null);
      },
      (error) => console.error('Error loading visit/form mapping:', error),
    );
    return () => unsubscribe();
  }, [projectId]);

  // Listen to dataManagement/plan (for DMP, edit checks)
  useEffect(() => {
    if (!projectId) return;

    const unsubscribe = onSnapshot(
      dataManagementDoc(projectId, 'plan'),
      (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data();
          setPlan(data);
        }
        setLoading(false);
      },
      (error) => {
        console.error('Error loading data management plan:', error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [projectId]);

  // Listen to dataManagement/dmp (comprehensive DMP)
  useEffect(() => {
    if (!projectId) return;

    const unsubscribe = onSnapshot(
      dataManagementDoc(projectId, 'dmp'),
      (snapshot) => {
        if (snapshot.exists()) {
          setDmpDoc(snapshot.data());
        }
      },
      (error) => {
        console.error('Error loading comprehensive DMP:', error);
      }
    );

    return () => unsubscribe();
  }, [projectId]);

  // Listen to dataManagement/editCheckSpec
  useEffect(() => {
    if (!projectId) return;

    const unsubscribe = onSnapshot(
      dataManagementDoc(projectId, 'editCheckSpec'),
      (snapshot) => {
        if (snapshot.exists()) {
          setEditCheckSpec(snapshot.data());
        }
      },
      (error) => {
        console.error('Error loading edit check spec:', error);
      }
    );

    return () => unsubscribe();
  }, [projectId]);


  // Listen to dataManagement/dataDictionary (separate document for spec-based dict)
  useEffect(() => {
    if (!projectId) return;

    const unsubscribe = onSnapshot(
      dataManagementDoc(projectId, 'dataDictionary'),
      (snapshot) => {
        if (snapshot.exists()) {
          setDictData(snapshot.data());
        }
      },
      (error) => {
        console.error('Error loading data dictionary:', error);
      }
    );

    return () => unsubscribe();
  }, [projectId]);

  // Listen to dataManagement/dictionaryAdoption — its adoptedAt drives the
  // "regenerate it" notice on every dictionary-built card.
  useEffect(() => {
    if (!projectId) return;

    const unsubscribe = onSnapshot(
      dataManagementDoc(projectId, 'dictionaryAdoption'),
      (snapshot) => {
        setAdoption(snapshot.exists() ? snapshot.data() : null);
      },
      (error) => {
        console.error('Error loading dictionary adoption:', error);
      }
    );

    return () => unsubscribe();
  }, [projectId]);

  // An adoption completed before dictionaryAdoption carried lastCompleted:
  // read its history record once, so the Data Dictionary card can still say
  // why the dictionary is smaller than the sponsor's file.
  const legacyHistoryId: string | null =
    adoption?.completedAt && !adoption?.lastCompleted && typeof adoption?.historyId === 'string'
      ? adoption.historyId : null;
  useEffect(() => {
    if (!projectId || !legacyHistoryId) {
      setAdoptionHistory(null);
      return;
    }
    let cancelled = false;
    getDoc(doc(db, 'projects', projectId, 'dataManagement', 'dataDictionary', 'adoptionHistory', legacyHistoryId))
      .then((snap) => { if (!cancelled) setAdoptionHistory(snap.exists() ? snap.data() : null); })
      .catch((error) => {
        console.error('Error loading dictionary adoption history:', error);
        if (!cancelled) setAdoptionHistory(null);
      });
    return () => { cancelled = true; };
  }, [projectId, legacyHistoryId]);

  // Listen to dataManagement/sdtmMapping
  useEffect(() => {
    if (!projectId) return;

    const unsubscribe = onSnapshot(
      dataManagementDoc(projectId, 'sdtmMapping'),
      (snapshot) => {
        if (snapshot.exists()) {
          setSdtmDoc(snapshot.data());
        }
      },
      (error) => {
        console.error('Error loading SDTM Mapping:', error);
      }
    );

    return () => unsubscribe();
  }, [projectId]);

  // Listen to dataManagement/edts
  useEffect(() => {
    if (!projectId) return;
    const unsubscribe = onSnapshot(
      dataManagementDoc(projectId, 'edts'),
      (snapshot) => {
        if (snapshot.exists()) {
          setEdtsDoc(snapshot.data());
        }
      },
      (error) => {
        console.error('Error loading EDTS:', error);
      }
    );
    return () => unsubscribe();
  }, [projectId]);

  // Listen to dataManagement/medicalCodingSpec
  useEffect(() => {
    if (!projectId) return;
    const unsubscribe = onSnapshot(
      dataManagementDoc(projectId, 'medicalCodingSpec'),
      (snapshot) => {
        if (snapshot.exists()) {
          setMcsDoc(snapshot.data());
        }
      },
      (error) => console.error('Error loading MCS:', error)
    );
    return () => unsubscribe();
  }, [projectId]);

  // Listen to dataManagement/drcp
  useEffect(() => {
    if (!projectId) return;
    const unsubscribe = onSnapshot(
      dataManagementDoc(projectId, 'drcp'),
      (snapshot) => {
        if (snapshot.exists()) {
          setDrcpDoc(snapshot.data());
        }
      },
      (error) => console.error('Error loading DRCP:', error)
    );
    return () => unsubscribe();
  }, [projectId]);

  // Listen to dataManagement/saerp
  useEffect(() => {
    if (!projectId) return;
    const unsubscribe = onSnapshot(
      dataManagementDoc(projectId, 'saerp'),
      (snapshot) => {
        if (snapshot.exists()) {
          setSaerpDoc(snapshot.data());
        }
      },
      (error) => console.error('Error loading SAERP:', error)
    );
    return () => unsubscribe();
  }, [projectId]);

  // Listen to dataManagement/dblc
  useEffect(() => {
    if (!projectId) return;
    const unsubscribe = onSnapshot(
      dataManagementDoc(projectId, 'dblc'),
      (snapshot) => {
        if (snapshot.exists()) {
          setDblcDoc(snapshot.data());
        }
      },
      (error) => console.error('Error loading DBLC:', error)
    );
    return () => unsubscribe();
  }, [projectId]);

  // Listen to dataManagement/crfInstructionGuide
  useEffect(() => {
    if (!projectId) return;

    const unsubscribe = onSnapshot(
      dataManagementDoc(projectId, 'crfInstructionGuide'),
      async (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data();
          // Publish the metadata immediately so the card resolves to "Ready"
          // (Download) without waiting on the chunked-content fetch below —
          // otherwise the card flashes the "Generate" buttons for the seconds
          // it takes to load the chunks.
          setGuideData(data);
          setGuideLoaded(true);
          if (data.chunked && !data.content) {
            // Load chunks from subcollection (content is used by the legacy
            // download/preview paths; the v2 download re-fetches server-side).
            try {
              const chunksSnap = await getDocs(
                query(
                  crfInstructionGuideChunksCollection(projectId),
                )
              );
              const chunks = chunksSnap.docs
                .sort((a, b) => (a.data().index || 0) - (b.data().index || 0))
                .map(d => d.data().content)
                .join('');
              setGuideData((prev: any) => ({ ...(prev || {}), ...data, content: chunks }));
            } catch (err) {
              console.error('Error loading guide chunks:', err);
            }
          }
        } else {
          // Doc absent → genuinely not generated yet.
          setGuideData(null);
          setGuideLoaded(true);
        }
      },
      (error) => {
        console.error('Error loading CRF instruction guide:', error);
      }
    );

    return () => unsubscribe();
  }, [projectId]);

  // ── CRF Instruction Guide background-job progress subscription ──────
  // Watch the pointer doc (dataManagement/crfGuideJob); whenever it names a
  // jobId, (re)subscribe to that job's status doc (guideJobs/{jobId}) and
  // mirror it into `guideJob`. This also auto-resumes on mount/refresh: if
  // a pointer already exists for an active job, we reattach to its
  // progress. The pointer changing jobId (a Restart supersedes the old
  // job) swaps the inner subscription to the new job.
  useEffect(() => {
    if (!projectId) return;
    let jobUnsub: (() => void) | null = null;
    let watchedJobId: string | null = null;

    const stopJobWatch = () => {
      if (jobUnsub) {
        jobUnsub();
        jobUnsub = null;
      }
      watchedJobId = null;
    };

    const pointerUnsub = onSnapshot(
      crfGuideJobPointerDoc(projectId),
      (snap) => {
        const jobId: string | undefined = snap.exists() ? snap.data().jobId : undefined;
        if (!jobId) {
          stopJobWatch();
          return;
        }
        if (jobId === watchedJobId) return; // already watching this job
        stopJobWatch();
        watchedJobId = jobId;
        guideJobDoneRef.current = null;
        setGuideJobStalled(false);
        // Only toast "ready" when the guide actually finishes DURING this
        // session — i.e. we saw it rendering/finalizing first. If the first
        // snapshot we get for a job is already 'done' (a page refresh of an
        // already-finished guide), stay quiet. Captured per watched job.
        let jobBecameActive = false;
        jobUnsub = onSnapshot(
          guideJobDoc(projectId, jobId),
          (jobSnap) => {
            if (!jobSnap.exists()) return;
            const d = jobSnap.data() as any;
            const status = d.status as
              | 'rendering'
              | 'finalizing'
              | 'done'
              | 'error'
              | 'superseded';

            // A newer job replaced this one — stop reflecting it.
            if (status === 'superseded') {
              setGuideJob((prev) => (prev?.jobId === jobId ? null : prev));
              return;
            }

            const updatedAtMs = timestampToMillis(d.updatedAt);
            setGuideJobStalled(false); // fresh write — not stalled
            setGuideJob({
              jobId,
              status,
              formsTotal: d.formsTotal,
              formsDone: d.formsDone,
              formsFailed: d.formsFailed,
              quickMode: d.quickMode,
              errorRef: d.errorRef,
              errorMessage: d.errorMessage,
              updatedAtMs,
              createdAtMs: timestampToMillis(d.createdAt),
            });

            // Reflect the job into the per-card generating state so the
            // card shows progress (and disables the buttons) while active,
            // and clears once the job lands.
            if (status === 'rendering' || status === 'finalizing') {
              jobBecameActive = true;
              setGeneratingDocs((prev) => {
                if (prev.has('crfInstructionGuide')) return prev;
                return new Set(prev).add('crfInstructionGuide');
              });
            } else {
              setGeneratingDocs((prev) => {
                if (!prev.has('crfInstructionGuide')) return prev;
                const next = new Set(prev);
                next.delete('crfInstructionGuide');
                return next;
              });
            }

            if (status === 'done') {
              if (guideJobDoneRef.current !== jobId) {
                guideJobDoneRef.current = jobId;
                setGuideError(null);
                // Suppress on a plain refresh of an already-finished guide;
                // only announce a guide that finished while being watched.
                if (jobBecameActive) {
                  showToast('CRF Instruction Guide is ready.', 'success');
                }
              }
            } else if (status === 'error') {
              const message =
                d.errorMessage ||
                'CRF instruction guide generation failed. Please try again.';
              setGuideError({ message, errorRef: d.errorRef });
            }
          },
          (err) => console.error('Error watching CRF guide job:', err),
        );
      },
      (err) => console.error('Error watching CRF guide job pointer:', err),
    );

    return () => {
      pointerUnsub();
      stopJobWatch();
    };
  }, [projectId, showToast]);

  // Stalled detection — while a job is actively rendering/finalizing, poll
  // its last updatedAt against the wall clock. If it hasn't advanced for
  // ~15 min, flag it so the card can offer a Restart. The interval is torn
  // down once the job is no longer active (done/error/superseded/none).
  useEffect(() => {
    const active =
      guideJob &&
      (guideJob.status === 'rendering' || guideJob.status === 'finalizing');
    if (!active) {
      setGuideJobStalled(false);
      return;
    }
    const check = () => {
      const last = guideJob.updatedAtMs;
      if (last > 0 && Date.now() - last > GUIDE_JOB_STALL_MS) {
        setGuideJobStalled(true);
      }
    };
    check();
    const id = setInterval(check, 30_000);
    return () => clearInterval(id);
  }, [guideJob]);

  // Tick once a second while a job is rendering/finalizing so the elapsed-time
  // display updates live. Torn down once the job is no longer active.
  useEffect(() => {
    const active =
      guideJob &&
      (guideJob.status === 'rendering' || guideJob.status === 'finalizing');
    if (!active) return;
    setJobNowMs(Date.now());
    const id = setInterval(() => setJobNowMs(Date.now()), 1000);
    return () => clearInterval(id);
  }, [guideJob]);

  // A generation is genuinely in flight when EITHER this browser just started it
  // (local `generatingDocs`) OR the server's own marker says so. The marker
  // (`_generating` + `_generationStartedAt`, written by markGenerationStarted and
  // cleared when the full document is written) is the source of truth: it survives
  // a page reload and — critically — cannot be torn down by an unrelated or
  // duplicate call's error/finally handler, which is what left a live generation
  // showing a clickable "Generate" button and a red error. A marker older than the
  // staleness window is treated as dead so a crashed run can't wedge the card.
  // The window MUST exceed the slowest legitimate generation: SDTM mapping runs
  // at the Cloud Function's 30-min (1800s) ceiling on large studies, so a 15-min
  // window would wrongly declare a still-running SDTM job dead and re-enable the
  // button mid-generation. 35 min clears a genuinely crashed marker while never
  // cutting off a live run.
  const GENERATION_STALE_MS = GENERATION_LOCK_STALE_MS;
  const markerGenerating = (doc: any): boolean => {
    if (!doc?._generating) return false;
    const startedAt = doc?._generationStartedAt;
    if (!startedAt?.toDate) return true; // marker set, timestamp not yet materialized
    return Date.now() - startedAt.toDate().getTime() < GENERATION_STALE_MS;
  };
  const isGen = (id: DocumentId, doc: any): boolean =>
    generatingDocs.has(id) || markerGenerating(doc);

  /**
   * Codes the callable SDK produces when NO real response came back — the
   * request was cut off rather than answered. 504 maps to `deadline-exceeded`;
   * an aborted connection maps to `internal`/`unknown`.
   */
  const NO_RESPONSE_CODES = new Set([
    'internal', 'deadline-exceeded', 'unavailable', 'aborted', 'cancelled', 'unknown',
  ]);

  /**
   * Turn a generation failure into an honest message.
   *
   * Two conditions are NOT failures and must never show a red error:
   *
   *  1. "already in progress" — a generation is genuinely running.
   *
   *  2. The request outran the HTTP ceiling. A Cloud Function request is capped
   *     at 30 min, but the function keeps executing past it: SDTM mapping on a
   *     1,631-variable study returned 504 at 30 min and then WROTE ITS DOCUMENT
   *     SUCCESSFULLY at ~43 min. The browser had already been told "internal".
   *     Our own server errors always carry a human sentence (+ reference code)
   *     via internalErrorMessage, so a message that is empty or merely echoes
   *     the code means no real response arrived — the work is almost certainly
   *     still running, NOT failed. The card keeps showing "Generating…" from the
   *     server marker and flips to Generated on its own when the write lands.
   */
  const reportGenerationError = (err: any, fallback: string) => {
    const message = String(err?.message || '').trim();
    const code = String(err?.code || '').replace(/^functions\//, '').trim().toLowerCase();

    if (/already in progress/i.test(message)) {
      showToast('This document is already generating — it can take a few minutes. Please wait.', 'info');
      return;
    }

    const noRealResponse = !message || message.toLowerCase() === code;
    if (noRealResponse && NO_RESPONSE_CODES.has(code)) {
      showToast(
        'This is taking longer than the browser will wait. It’s still generating in the background — this page updates on its own when it finishes. Please don’t press Generate again.',
        'info',
      );
      return;
    }

    showToast(message || fallback, 'error');
  };

  // Derive statuses from data
  useEffect(() => {
    setDocumentStatuses(prev => ({
      ...prev,
      dataDictionary: isGen('dataDictionary', dictData)
        ? 'generating'
        : dictData?.fieldCount > 0 ? 'generated' : 'not_started',
      editChecks: isGen('editChecks', editCheckSpec)
        ? 'generating'
        : (editCheckSpec?.checkCount > 0 || plan?.editChecks?.length > 0) ? 'generated' : 'not_started',
      dataManagementPlan: isGen('dataManagementPlan', dmpDoc)
        ? 'generating'
        : (dmpDoc?.sections?.length > 0 || plan?.dataRequirements || plan?.validationRules) ? 'generated' : 'not_started',
      crfInstructionGuide: isGen('crfInstructionGuide', guideData)
        ? 'generating'
        : (guideData?.content || guideData?.chunked || guideData?.docxBase64 || guideData?.version === 2) ? 'generated' : 'not_started',
      sdtmMapping: isGen('sdtmMapping', sdtmDoc)
        ? 'generating'
        : (sdtmDoc && (sdtmDoc.version || sdtmDoc.title)) ? 'generated' : 'not_started',
      edts: isGen('edts', edtsDoc)
        ? 'generating'
        : (edtsDoc && (edtsDoc.version || edtsDoc.title)) ? 'generated' : 'not_started',
      medicalCodingSpec: isGen('medicalCodingSpec', mcsDoc)
        ? 'generating'
        : (mcsDoc && (mcsDoc.version || mcsDoc.title)) ? 'generated' : 'not_started',
      drcp: isGen('drcp', drcpDoc)
        ? 'generating'
        : (drcpDoc && (drcpDoc.version || drcpDoc.title)) ? 'generated' : 'not_started',
      saerp: isGen('saerp', saerpDoc)
        ? 'generating'
        : (saerpDoc && (saerpDoc.version || saerpDoc.title)) ? 'generated' : 'not_started',
      dblc: isGen('dblc', dblcDoc)
        ? 'generating'
        : (dblcDoc && (dblcDoc.version || dblcDoc.title)) ? 'generated' : 'not_started',
    }));
  }, [plan, dmpDoc, editCheckSpec, dictData, guideData, sdtmDoc, edtsDoc, mcsDoc, drcpDoc, saerpDoc, dblcDoc, generatingDocs]);

  // Progress simulation for long-running generation tasks
  const startProgress = (docId: DocumentId, steps: string[]) => {
    let stepIdx = 0;
    const now = Date.now();
    setGenerationProgress({ docId, step: 0, message: steps[0], percent: 5, startedAt: now });
    setElapsedSeconds(0);

    if (progressTimerRef.current) clearInterval(progressTimerRef.current);
    progressTimerRef.current = setInterval(() => {
      stepIdx++;
      if (stepIdx < steps.length) {
        const percent = Math.min(90, Math.round(((stepIdx + 1) / steps.length) * 85) + 5);
        setGenerationProgress(prev => ({ ...prev, step: stepIdx, message: steps[stepIdx], percent }));
      }
    }, 8000); // Advance every 8 seconds

    if (elapsedTimerRef.current) clearInterval(elapsedTimerRef.current);
    elapsedTimerRef.current = setInterval(() => {
      setElapsedSeconds(Math.floor((Date.now() - now) / 1000));
    }, 1000);
  };

  const stopProgress = () => {
    if (progressTimerRef.current) {
      clearInterval(progressTimerRef.current);
      progressTimerRef.current = null;
    }
    if (elapsedTimerRef.current) {
      clearInterval(elapsedTimerRef.current);
      elapsedTimerRef.current = null;
    }
    setGenerationProgress({ docId: null, step: 0, message: '', percent: 0, startedAt: 0 });
    setElapsedSeconds(0);
  };

  // Cleanup timers on unmount
  useEffect(() => () => {
    if (progressTimerRef.current) clearInterval(progressTimerRef.current);
    if (elapsedTimerRef.current) clearInterval(elapsedTimerRef.current);
  }, []);

  // Generate Data Dictionary (calls new spec-based function)
  const handleGenerateDataDictionary = async () => {
    setGeneratingDocs(prev => new Set(prev).add('dataDictionary'));
    startProgress('dataDictionary', [
      'Loading CRF forms...',
      'Analyzing form structure...',
      'Mapping CDASH variables...',
      'Processing field types and validation rules...',
      'Generating data dictionary entries...',
      'Building CSV output...',
      'Finalizing...',
    ]);
    try {
      // 31-min client timeout (GENERATOR_XLARGE) — gives a 60s buffer over
      // the Gen-2 server's 1800s ceiling so the browser SDK doesn't surface
      // a false `deadline-exceeded` at the moment the server times out.
      // Bumped from XLONG (10 min, paired with the Gen-1 540s server ceiling)
      // to GENERATOR_XLARGE as part of the 2026-05-22 Wave 2 Gen-2 migration.
      const generateFn = httpsCallable(functions, CLOUD_FUNCTIONS.GENERATE_SPEC_DATA_DICTIONARY_V2, { timeout: CALLABLE_TIMEOUTS.GENERATOR_XLARGE });
      await generateFn({ projectId });
    } catch (err: any) {
      console.error('Error generating data dictionary:', err);
      reportGenerationError(err, 'Failed to generate data dictionary. Please try again.');
    } finally {
      stopProgress();
      setGeneratingDocs(prev => {
        const next = new Set(prev);
        next.delete('dataDictionary');
        return next;
      });
    }
  };

  // Generate Edit Check Specification (calls new Cloud Function)
  const handleGenerateEditChecks = async () => {
    setGeneratingDocs(prev => new Set(prev).add('editChecks'));
    setExpandedDocId('editChecks');
    startProgress('editChecks', [
      'Loading protocol and CRF data...',
      'Generating endpoint checks...',
      'Generating safety checks...',
      'Generating protocol compliance checks...',
      'Generating general checks...',
      'Writing checks from your data dictionary...',
      'Storing edit check specification...',
      'Finalizing...',
    ]);
    try {
      const generateFn = httpsCallable(functions, CLOUD_FUNCTIONS.GENERATE_EDIT_CHECK_SPECIFICATION_V2, { timeout: CALLABLE_TIMEOUTS.GENERATOR_XLARGE });
      await generateFn({ projectId });
    } catch (err: any) {
      console.error('Error generating edit checks:', err);
      reportGenerationError(err, 'Failed to generate edit checks. Please try again.');
    } finally {
      stopProgress();
      setGeneratingDocs(prev => {
        const next = new Set(prev);
        next.delete('editChecks');
        return next;
      });
    }
  };

  // Generate comprehensive DMP (calls new Cloud Function directly from hub card)
  const handleGenerateDMP = async () => {
    setGeneratingDocs(prev => new Set(prev).add('dataManagementPlan'));
    startProgress('dataManagementPlan', [
      'Gathering protocol and study data...',
      'Generating Study Context sections...',
      'Generating Data Definition sections...',
      'Generating Data Processing sections...',
      'Generating Compliance sections...',
      'Storing DMP sections...',
      'Finalizing document...',
    ]);
    try {
      const generateFn = httpsCallable(functions, CLOUD_FUNCTIONS.GENERATE_COMPREHENSIVE_DMP_V2, { timeout: CALLABLE_TIMEOUTS.GENERATOR_XLARGE });
      const result = await generateFn({ projectId });
      const data = result.data as any;
      if (data.success) {
        // Auto-expand the panel to show the generated DMP
        setExpandedDocId('dataManagementPlan');
      }
    } catch (err: any) {
      console.error('Error generating DMP:', err);
      reportGenerationError(err, 'Failed to generate Data Management Plan. Please try again.');
    } finally {
      stopProgress();
      setGeneratingDocs(prev => {
        const next = new Set(prev);
        next.delete('dataManagementPlan');
        return next;
      });
    }
  };

  // Start CRF Instruction Guide as a BACKGROUND JOB (primary "Generate").
  //
  // startCrfGuideJob returns quickly with `{ jobId }` and the long render
  // runs server-side; the pointer/job-doc subscription above drives the
  // real progress bar, success toast, error surfacing, and stalled note.
  // We do NOT use the simulated stepper here — progress is real.
  //
  // The callable may instead return a structured precondition payload
  // (rather than throwing) so it can NAME the affected forms:
  //  - CRF_RENDERINGS_MISSING:   forms with no embeddable PDF rendering
  //  - CRF_RENDERINGS_DUPLICATE: forms whose page rendering duplicates
  //    another form's (a wrong/shared source page). NOTE: on the job path
  //    duplicateForms is an array of { formCode, formName } objects (the
  //    sync path returns string[]); formLabels() handles both.
  // On a hard failure (no forms / a job already running) it THROWS an
  // HttpsError whose message we surface via toast.
  const handleStartGuideJob = async () => {
    if (!projectId) return;
    setGuideError(null);
    setGuideJobStalled(false);
    // Optimistically reflect "generating" so the button locks immediately;
    // the job-doc subscription takes over once the first write lands.
    setGeneratingDocs(prev => new Set(prev).add('crfInstructionGuide'));
    try {
      const startFn = httpsCallable(functions, CLOUD_FUNCTIONS.START_CRF_GUIDE_JOB, { timeout: CALLABLE_TIMEOUTS.SHORT });
      const result: any = await startFn({ projectId });
      const data = result?.data;

      if (data?.error === 'CRF_RENDERINGS_MISSING') {
        const message = data.message || 'Some forms have not been rendered yet. Use Generate CRF (or Generate All CRFs) in the CRF Builder.';
        setGuideError({ message, forms: formLabels(data.missingForms) });
        showToast(message, 'error');
        setGeneratingDocs(prev => { const n = new Set(prev); n.delete('crfInstructionGuide'); return n; });
        return;
      }
      if (data?.error === 'CRF_RENDERINGS_DUPLICATE') {
        const message = data.message || 'Some forms render to the same page image. Use Generate CRF in the CRF Builder to give each form its own page.';
        setGuideError({ message, forms: formLabels(data.duplicateForms) });
        showToast(message, 'error');
        setGeneratingDocs(prev => { const n = new Set(prev); n.delete('crfInstructionGuide'); return n; });
        return;
      }
      // Success — a job started. Leave the generating flag set; the
      // pointer/job subscription will reflect live progress and clear it
      // when the job lands. (data.jobId is available but we let the
      // pointer doc be the source of truth for which job to watch.)
    } catch (err: any) {
      console.error('Error starting CRF instruction guide job:', err);
      const message = err?.message || 'Failed to start CRF instruction guide generation. Please try again.';
      showToast(message, 'error');
      setGeneratingDocs(prev => { const n = new Set(prev); n.delete('crfInstructionGuide'); return n; });
    }
  };

  // Quick generate (faster, draft quality) — runs the EXISTING synchronous
  // generator on the flash model (quickMode → flash, mapped server-side).
  // This path keeps the simulated stepper, since it blocks until the guide
  // lands at dataManagement/crfInstructionGuide (read by the existing
  // listener / download code).
  const handleQuickGenerateGuide = async () => {
    setGeneratingDocs(prev => new Set(prev).add('crfInstructionGuide'));
    setGuideError(null);
    startProgress('crfInstructionGuide', [
      'Loading protocol and CRF forms...',
      'Generating front matter and conventions...',
      'Writing field-by-field instructions...',
      'Processing form completion guidelines...',
      'Generating visit schedule mapping...',
      'Compiling abbreviations and definitions...',
      'Assembling final document...',
      'Finalizing...',
    ]);
    try {
      const generateFn = httpsCallable(functions, CLOUD_FUNCTIONS.GENERATE_CRF_INSTRUCTION_GUIDE_V2, { timeout: CALLABLE_TIMEOUTS.GENERATOR_XLARGE });
      const result: any = await generateFn({ projectId, quickMode: true });
      // Structured precondition errors (sync path: string[] form lists).
      if (result?.data?.error === 'CRF_RENDERINGS_MISSING') {
        const message = result.data.message || 'Some forms have not been rendered yet. Use Generate CRF (or Generate All CRFs) in the CRF Builder.';
        setGuideError({ message, forms: formLabels(result.data.missingForms) });
        showToast(message, 'error');
        return;
      }
      if (result?.data?.error === 'CRF_RENDERINGS_DUPLICATE') {
        const message = result.data.message || 'Some forms render to the same page image. Use Generate CRF in the CRF Builder to give each form its own page.';
        setGuideError({ message, forms: formLabels(result.data.duplicateForms) });
        showToast(message, 'error');
        return;
      }
      // Success — clear any previous error.
      setGuideError(null);
    } catch (err: any) {
      console.error('Error generating CRF instruction guide (quick):', err);
      const message = err?.message || 'Failed to generate CRF instruction guide. Please try again.';
      setGuideError({ message });
      showToast(message, 'error');
    } finally {
      stopProgress();
      setGeneratingDocs(prev => {
        const next = new Set(prev);
        next.delete('crfInstructionGuide');
        return next;
      });
    }
  };

  // Generate SDTM Mapping Specification (calls cloud function directly from hub card,
  // matching the DMP/Edit Checks pattern so progress shows inside the card)
  const handleGenerateSDTMMapping = async () => {
    setGeneratingDocs(prev => new Set(prev).add('sdtmMapping'));
    startProgress('sdtmMapping', [
      'Loading data dictionary and CRF forms...',
      'Resolving SDTM/SDTMIG versions from study start date...',
      'Phase 1: looking up CRF variables in CDASH library...',
      'Phase 1: mapping unknown variables...',
      'Backfilling data dictionary with SDTM mappings...',
      'Phase 2: generating regulatory framework section...',
      'Phase 2: generating timing variable derivations...',
      'Phase 2: generating trial design domain narratives...',
      'Phase 2: generating reviewer\u2019s guide notes...',
      'Finalizing document...',
    ]);
    try {
      const generateFn = httpsCallable(functions, CLOUD_FUNCTIONS.GENERATE_SDTM_MAPPING_V2, { timeout: CALLABLE_TIMEOUTS.GENERATOR_XLARGE });
      const result = await generateFn({ projectId });
      const data = result.data as any;
      if (data?.success) {
        // Auto-expand the panel to show the generated mapping
        setExpandedDocId('sdtmMapping');
      }
    } catch (err: any) {
      console.error('Error generating SDTM Mapping:', err);
      reportGenerationError(err, 'Failed to generate SDTM Mapping. Please try again.');
    } finally {
      stopProgress();
      setGeneratingDocs(prev => {
        const next = new Set(prev);
        next.delete('sdtmMapping');
        return next;
      });
    }
  };

  // Generate EDTS (calls cloud function directly from hub card,
  // matching the DMP/Edit Checks/SDTM Mapping pattern so progress shows inside the card)
  const handleGenerateEDTS = async () => {
    setGeneratingDocs(prev => new Set(prev).add('edts'));
    startProgress('edts', [
      'Loading protocol and study setup...',
      'Detecting external data sources from protocol facts...',
      'Generating regulatory framework section...',
      'Generating per-source variable metadata...',
      'Generating file format and transfer schedule sections...',
      'Generating transmission, security, and blinding sections...',
      'Generating validation and reconciliation sections...',
      'Generating special cases and retention sections...',
      'Building signature page...',
      'Finalizing document...',
    ]);
    try {
      const generateFn = httpsCallable(functions, CLOUD_FUNCTIONS.GENERATE_EDTS_V2, { timeout: CALLABLE_TIMEOUTS.GENERATOR_XLARGE });
      const result = await generateFn({ projectId });
      const data = result.data as any;
      if (data?.success) {
        setExpandedDocId('edts');
      }
    } catch (err: any) {
      console.error('Error generating EDTS:', err);
      reportGenerationError(err, 'Failed to generate EDTS. Please try again.');
    } finally {
      stopProgress();
      setGeneratingDocs(prev => {
        const next = new Set(prev);
        next.delete('edts');
        return next;
      });
    }
  };

  // Generate Medical Coding Specification (calls cloud function directly from hub card,
  // matching the EDTS/UAT pattern so progress shows inside the card)
  const handleGenerateMCS = async () => {
    setGeneratingDocs(prev => new Set(prev).add('medicalCodingSpec'));
    startProgress('medicalCodingSpec', [
      'Loading protocol and study setup...',
      'Scanning CRFs for coding domains...',
      'Detecting dictionary requirements...',
      'Generating dictionary specifications...',
      'Generating coding conventions...',
      'Generating per-domain coding specifications...',
      'Generating quality control sections...',
      'Generating appendices...',
      'Finalizing document...',
    ]);
    try {
      const generateFn = httpsCallable(functions, CLOUD_FUNCTIONS.GENERATE_MCS_V2, { timeout: CALLABLE_TIMEOUTS.GENERATOR_XLARGE });
      const result = await generateFn({ projectId });
      const data = result.data as any;
      if (data?.success) {
        setExpandedDocId('medicalCodingSpec');
      }
    } catch (err: any) {
      console.error('Error generating MCS:', err);
      reportGenerationError(err, 'Failed to generate Medical Coding Specification. Please try again.');
    } finally {
      stopProgress();
      setGeneratingDocs(prev => {
        const next = new Set(prev);
        next.delete('medicalCodingSpec');
        return next;
      });
    }
  };

  // Generate Database Lock Checklist
  const handleGenerateDBLC = async () => {
    setGeneratingDocs(prev => new Set(prev).add('dblc'));
    startProgress('dblc', [
      'Loading protocol and study setup...',
      'Reading data dictionary and detecting coding scope...',
      'Resolving external data sources...',
      'Authoring lock readiness thresholds...',
      'Building 19-category checklist...',
      'Tailoring conditional items to study scope...',
      'Composing approval signatory list...',
      'Drafting post-lock activities and unlock governance...',
      'Assembling final document...',
    ]);
    try {
      const generateFn = httpsCallable(functions, CLOUD_FUNCTIONS.GENERATE_DATABASE_LOCK_CHECKLIST_V2, { timeout: CALLABLE_TIMEOUTS.GENERATOR_XLARGE });
      const result = await generateFn({ projectId });
      const data = result.data as any;
      if (data?.success) {
        setExpandedDocId('dblc');
      }
    } catch (err: any) {
      console.error('Error generating DBLC:', err);
      reportGenerationError(err, 'Failed to generate Database Lock Checklist. Please try again.');
    } finally {
      stopProgress();
      setGeneratingDocs(prev => {
        const next = new Set(prev);
        next.delete('dblc');
        return next;
      });
    }
  };

  // Generate SAE Reconciliation Procedure
  const handleGenerateSAERP = async () => {
    setGeneratingDocs(prev => new Set(prev).add('saerp'));
    startProgress('saerp', [
      'Loading protocol and study setup...',
      'Reading data dictionary and isolating AE/SAE forms...',
      'Resolving safety database from study setup...',
      'Building reconciliation data point catalog...',
      'Authoring discrepancy tier framework...',
      'Drafting frequency and milestone schedule...',
      'Composing blinding and unblinding procedures...',
      'Defining DBL sign-off criteria...',
      'Assembling final document...',
    ]);
    try {
      const generateFn = httpsCallable(functions, CLOUD_FUNCTIONS.GENERATE_SAE_RECONCILIATION_PROCEDURE_V2, { timeout: CALLABLE_TIMEOUTS.GENERATOR_XLARGE });
      const result = await generateFn({ projectId });
      const data = result.data as any;
      if (data?.success) {
        setExpandedDocId('saerp');
      }
    } catch (err: any) {
      console.error('Error generating SAERP:', err);
      reportGenerationError(err, 'Failed to generate SAE Reconciliation Procedure. Please try again.');
    } finally {
      stopProgress();
      setGeneratingDocs(prev => {
        const next = new Set(prev);
        next.delete('saerp');
        return next;
      });
    }
  };

  // Generate Data Review and Cleaning Plan
  const handleGenerateDRCP = async () => {
    setGeneratingDocs(prev => new Set(prev).add('drcp'));
    startProgress('drcp', [
      'Loading protocol and study setup...',
      'Reading data dictionary and edit checks...',
      'Detecting external data sources...',
      'Identifying critical-to-quality factors...',
      'Authoring risk-based review strategy...',
      'Drafting query lifecycle and SLAs...',
      'Building KRI and QTL catalog...',
      'Defining database lock criteria...',
      'Assembling final document...',
    ]);
    try {
      const generateFn = httpsCallable(functions, CLOUD_FUNCTIONS.GENERATE_DATA_REVIEW_CLEANING_PLAN_V2, { timeout: CALLABLE_TIMEOUTS.GENERATOR_XLARGE });
      const result = await generateFn({ projectId });
      const data = result.data as any;
      if (data?.success) {
        setExpandedDocId('drcp');
      }
    } catch (err: any) {
      console.error('Error generating DRCP:', err);
      reportGenerationError(err, 'Failed to generate Data Review and Cleaning Plan. Please try again.');
    } finally {
      stopProgress();
      setGeneratingDocs(prev => {
        const next = new Set(prev);
        next.delete('drcp');
        return next;
      });
    }
  };

  // Route generate calls per document.
  //
  // IMPORTANT: this is called from BOTH the first-time "Generate" button AND
  // the "Regenerate" refresh icon on already-generated cards. In both cases
  // it must call the underlying generation function. Do NOT add a "doc already
  // exists -> just expand the panel" branch — that's the View button's job
  // (toggleDetail), and adding it here breaks the Regenerate button.
  const handleGenerate = (docId: DocumentId) => {
    // Never kick off a second generation while one is already in flight (per the
    // server marker or this browser). This is the concurrency guard that stops a
    // duplicate call from reaching the server and returning a scary "already in
    // progress" error. It does NOT short-circuit Regenerate on a finished doc —
    // that doc's status is 'generated', not 'generating'.
    if (documentStatuses[docId] === 'generating') {
      showToast('This document is already generating — it can take a few minutes. Please wait.', 'info');
      return;
    }
    if (docId === 'dataDictionary') {
      handleGenerateDataDictionary();
    } else if (docId === 'editChecks') {
      handleGenerateEditChecks();
    } else if (docId === 'dataManagementPlan') {
      handleGenerateDMP();
    } else if (docId === 'crfInstructionGuide') {
      handleStartGuideJob();
    } else if (docId === 'sdtmMapping') {
      handleGenerateSDTMMapping();
    } else if (docId === 'edts') {
      handleGenerateEDTS();
    } else if (docId === 'medicalCodingSpec') {
      handleGenerateMCS();
    } else if (docId === 'drcp') {
      handleGenerateDRCP();
    } else if (docId === 'saerp') {
      handleGenerateSAERP();
    } else if (docId === 'dblc') {
      handleGenerateDBLC();
    }
  };

  // Round-trip upload. Deliberately named and described so it cannot be
  // confused with the CRF Builder's REDCap "Import DD".
  const [showApplyDdEdits, setShowApplyDdEdits] = useState(false);
  const [showAdoptDd, setShowAdoptDd] = useState(false);

  // CRF Instruction Guide download (shared download path, features/downloads).
  // The filename is built here: `<StudyShortCode>_CRF_Completion_Guidelines_
  // YYYY-MM-DD.docx`. The reply is a signed URL today; a legacy base64 reply
  // is handled by the shared normaliser, and a legacy markdown reply is built
  // into Word here.
  const guideDownloadRequest = (): DownloadRequest => ({
    id: 'crfInstructionGuide:word',
    docName: DOWNLOAD_DOC_NAMES.crfInstructionGuide,
    precheck: () => downloadBlockedReason(projectId, 'crf_guide'),
    callable: { name: CLOUD_FUNCTIONS.DOWNLOAD_CRF_INSTRUCTION_GUIDE, args: { projectId } },
    filename: async (reply) => {
      // Study short code for the filename: prefer the value stamped on the
      // guide doc; fall back to the project's shortName/name for guides
      // generated before that field existed.
      const data = (reply ?? {}) as { studyShortName?: string };
      let studyShort: string = data.studyShortName || (guideData as any)?.studyShortName || '';
      if (!studyShort) {
        try {
          const psnap = await getDoc(projectDoc(projectId!));
          const pd: any = psnap.exists() ? psnap.data() : {};
          studyShort = pd.shortName || pd.name || '';
        } catch { /* fall back to "Study" */ }
      }
      return guideDownloadFileName(studyShort);
    },
    // Version 1 (legacy): markdown returned — convert client-side.
    onUnrecognised: async (reply) => {
      const content = (reply as { content?: string } | null)?.content;
      if (!content) throw new Error('the guide is empty.');

      const crfsSnap = await getDocs(
        query(
          artifactsCollection(projectId!),
          where('type', '==', 'crf'),
          where('status', 'in', ['generated', 'approved']),
        )
      );

      const { parseCRFForm, generateCRFCompletionGuidelinesDocx, generateDocx } =
        await import('../../utils/docxGenerator');

      const crfForms = crfsSnap.docs
        .map(d => parseCRFForm(d.data().content || ''))
        .filter((f): f is NonNullable<typeof f> => f !== null);

      if (crfForms.length > 0) {
        const projectSnap = await getDoc(projectDoc(projectId!));
        const projectData: any = projectSnap.exists() ? projectSnap.data() : {};
        const studySetup = projectData.studySetup || {};
        await generateCRFCompletionGuidelinesDocx(content, crfForms, {
          studyTitle: projectData.name || '',
          studyShortName: projectData.shortName || '',
          sponsor: studySetup.sponsor || '',
          protocolNumber: studySetup.protocolNumber || '',
          version: 'V1.0',
          documentDate: new Date().toLocaleDateString(),
          dateFormat: studyDateFormatFromDocs(
            projectData,
            crfsSnap.docs.map(d => d.data()),
          ),
        });
      } else {
        await generateDocx(content, 'CRF Completion Guidelines');
      }
      return 'saved';
    },
  });

  // The Download options shown on each card's face; empty when the card has
  // no download. The View panels read the same options from the registry.
  const cardDownloadOptions = (cardId: DocumentId): DownloadOption[] => {
    if (!projectId) return [];
    if (cardId === 'crfInstructionGuide') {
      return [{
        label: 'Word',
        title: 'Download the CRF Instruction Guide as a Word document',
        request: guideDownloadRequest(),
      }];
    }
    return studyDocumentDownloadOptions(cardId as DownloadCardId, { projectId, edcSystem });
  };

  const toggleDetail = (docId: DocumentId) => {
    setExpandedDocId(prev => prev === docId ? null : docId);
  };

  // Summary text for each card
  function getDocumentSummary(docId: DocumentId): string | null {
    switch (docId) {
      case 'dataDictionary': {
        // The line and any warning come from one summary (dictionarySizeSummary.ts).
        return summarizeDictionarySize({ dictionary: dictData, adoption, history: adoptionHistory }).line;
      }
      case 'editChecks': {
        const checks = plan?.editChecks || [];
        if (checks.length === 0) return null;
        const errors = checks.filter((c: EditCheck) => c.severity === 'error').length;
        const warnings = checks.filter((c: EditCheck) => c.severity === 'warning').length;
        return `${checks.length} edit checks (${errors} errors, ${warnings} warnings)`;
      }
      case 'dataManagementPlan': {
        const reqs = plan?.dataRequirements;
        if (!reqs && !plan?.validationRules) return null;
        if (reqs?.visits?.length || reqs?.assessments?.length) {
          return `Covers ${reqs.visits?.length || 0} visits, ${reqs.assessments?.length || 0} assessments`;
        }
        return 'Generated';
      }
      default:
        return null;
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  const editChecks: EditCheck[] = plan?.editChecks || [];

  // The stored document behind each card that records failed sections, so a
  // generated document with a failed section shows "Incomplete", not "Ready"
  // (docgen reasons, round 3). DRCP, SAERP, DBLC, the dictionary, the edit
  // checks and the CRF guide store no such field today.
  const incompleteSource: Partial<Record<DocumentId, unknown>> = {
    edts: edtsDoc,
    medicalCodingSpec: mcsDoc,
    sdtmMapping: sdtmDoc,
    dataManagementPlan: dmpDoc,
  };

  // Render an inline card (full functionality — generation, progress, view,
  // regenerate, panel below). The body is byte-identical to the existing
  // card-grid map function, just extracted as a helper so we can render inline
  // and extra cards in the same grouped layout.
  const renderInlineCard = (card: DocumentCardConfig) => {
    const Icon = card.icon;
    const status = documentStatuses[card.id];
    // Regenerate follows the same rule as Generate: no approved form, no run.
    const formsGate = approvedFormsGate(crfReady, needsApprovedForms(card.id));
    const summary = getDocumentSummary(card.id);
    const dictionarySizeWarnings = status === 'generated' && card.id === 'dataDictionary'
      ? summarizeDictionarySize({ dictionary: dictData, adoption, history: adoptionHistory }).warnings
      : [];
    const isExpanded = expandedDocId === card.id;
    const badge = hubBadge(status, incompleteSource[card.id] as Record<string, unknown> | undefined);

    return (
      <div
        key={card.id}
        className={`bg-white rounded-xl border shadow-card hover:shadow-card-hover transition-all duration-200 flex flex-col overflow-hidden ${
          isExpanded
            ? 'border-primary-300 ring-1 ring-primary-100'
            : 'border-gray-100 hover:border-gray-200'
        }`}
      >
        {/* Top accent bar — blue when the document has been generated */}
        <div className={`h-1 ${status === 'generated' ? 'bg-blue-500' : 'bg-transparent'}`} />

        <div className="p-6 flex flex-col flex-1">
          {/* Top: Icon + Title + Status */}
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center space-x-3">
              <div className={`h-10 w-10 ${card.iconBgColor} rounded-xl flex items-center justify-center`}>
                <Icon className={`h-5 w-5 ${card.iconColor}`} />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">{card.title}</h3>
                {card.comingSoon && (
                  <span className="badge-neutral mt-1">Coming Soon</span>
                )}
                {/* Inc5 step E: the DD card orients with the latest
                    consistency-run state; the download gate re-verifies
                    server-side at click time. */}
                {card.id === 'dataDictionary' && projectId && (
                  <div className="mt-1">
                    <ConsistencyChip projectId={projectId} />
                  </div>
                )}
                {/* Inc6 step E: the six prose cards carry a live per-document
                    verification chip (drift/stale-name state from the latest
                    run) — the graduated form of the old static label. */}
                {PROSE_DOC_KEY_BY_CARD[card.id] && projectId && (
                  <div className="mt-1">
                    <ProseDocChip projectId={projectId} docKey={PROSE_DOC_KEY_BY_CARD[card.id]} />
                  </div>
                )}
                {/* The check that ran when the document was generated. */}
                {status === 'generated' && projectId && GENERATION_CHECK_DOCS.find((d) => d.cardId === card.id) && (
                  <div className="mt-1">
                    <GenerationCheckBadge
                      projectId={projectId}
                      docId={GENERATION_CHECK_DOCS.find((d) => d.cardId === card.id)!.docId}
                    />
                  </div>
                )}
              </div>
            </div>
            {badge?.kind === 'ready' && (
              <span className="badge-success">
                <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
                {badge.label}
              </span>
            )}
            {badge?.kind === 'incomplete' && (
              <span
                className="inline-flex items-center rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-700"
                title="Some sections could not be generated. Open the document to see which, and regenerate them."
              >
                <AlertCircle className="h-3.5 w-3.5 mr-1" />
                {badge.label}
              </span>
            )}
            {badge?.kind === 'generating' && (
              <span className="badge-generating">
                <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                {badge.label}
              </span>
            )}
          </div>

          {/* Description */}
          <p className="text-sm text-gray-500 mb-4 flex-1">{card.description}</p>

        {/* Summary when generated */}
        {status === 'generated' && summary && (
          <p className="text-sm text-gray-700 mb-2 font-medium">{summary}</p>
        )}

        {/* Why the dictionary is smaller than the sponsor's file, when it is
            (dictionarySizeSummary.ts). Only the non-zero parts are named. */}
        {dictionarySizeWarnings.length > 0 && (
          <div
            className="mb-2 flex items-start space-x-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800"
            data-testid="dictionary-size-warning"
          >
            <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <p>{dictionarySizeWarnings.join(' ')}</p>
          </div>
        )}

        {/* Show generated date/time for documents that have been generated */}
        {status === 'generated' && (() => {
          const tsMap: Partial<Record<DocumentId, any>> = {
            dataDictionary: dictData?.generatedAt,
            crfInstructionGuide: guideData?.generatedAt,
            dataManagementPlan: dmpDoc?.generatedAt,
            editChecks: editCheckSpec?.generatedAt,
            sdtmMapping: sdtmDoc?.generatedAt,
            edts: edtsDoc?.generatedAt,
            medicalCodingSpec: mcsDoc?.generatedAt,
            drcp: drcpDoc?.generatedAt,
            saerp: saerpDoc?.generatedAt,
            dblc: dblcDoc?.generatedAt,
          };
          const ts = tsMap[card.id];
          if (!ts) return null;
          const dateStr = ts.toDate ? ts.toDate().toLocaleString() : (ts.seconds ? new Date(ts.seconds * 1000).toLocaleString() : 'recently');
          return <p className="text-xs text-gray-400 mb-4">Generated {dateStr}</p>;
        })()}

        {/* Built from an earlier data dictionary than the adopted one: say so until regenerated (design 2026-09-14 §5) */}
        {status === 'generated' && (() => {
          if (!DICTIONARY_DEPENDENT_DOCS.includes(card.id)) return null;
          // The whole document, not its date: an adopted dictionary may carry lastModifiedAt and no generatedAt.
          const docByCard: Partial<Record<DocumentId, any>> = {
            dataDictionary: dictData,
            editChecks: editCheckSpec,
            sdtmMapping: sdtmDoc,
            edts: edtsDoc,
            medicalCodingSpec: mcsDoc,
            drcp: drcpDoc,
            saerp: saerpDoc,
            dblc: dblcDoc,
            dataManagementPlan: dmpDoc,
          };
          const noticePath = RENAME_NOTICE_DOC_PATH[card.id];
          const notice = dictionaryChangedNotice(dictionaryChangedSince(adoption), docByCard[card.id], {
            renameNoticeOpen: !!noticePath && openRenameNotices.has(documentKeyOf(noticePath)),
          });
          if (!notice) return null;
          return <p className="text-xs text-amber-600 mb-4">{notice.text}</p>;
        })()}

        {/* What the document was built from has changed since: the same sentence its Word file opens with. */}
        {status === 'generated' && (() => {
          const text = freshnessNoticeFor(card.id, freshnessNotices);
          return text ? <p className="text-xs text-amber-600 mb-4">{text}</p> : null;
        })()}

        {/* A field renamed since this document was built: the user chooses Regenerate, Update field names only, or (backstop) Dismiss. */}
        {status === 'generated' && projectId && RENAME_NOTICE_DOC_PATH[card.id] && (
          <RenameNotice
            projectId={projectId}
            documentPath={RENAME_NOTICE_DOC_PATH[card.id]!}
            onRegenerate={() => handleGenerate(card.id)}
            regenerating={documentStatuses[card.id] === 'generating'}
            regenerateBlockedReason={formsGate.blocked ? formsGate.reason : null}
          />
        )}

        {/* Dependency warnings — show missing prerequisites before generation */}
        {status !== 'generated' && (() => {
          const warnings: string[] = [];
          const infos: string[] = [];
          // CRF dependencies — these need approved CRF forms to exist.
          // (The CRF Instruction Guide's no-approved-forms case is handled in the
          // action area below, with the same sentence and a link to the CRF
          // Builder, so it is skipped here to avoid saying it twice.)
          if (needsApprovedForms(card.id) && card.id !== 'crfInstructionGuide' && !hasApprovedForms) {
            // One rule, one sentence: whatever crfReadiness says the state is.
            warnings.push(crfReadinessMessage(crfReady));
          }
          // Some forms approved: generation is allowed, and the customer is told
          // how many forms the document will be built from.
          if (needsApprovedForms(card.id) && crfReady.partial) {
            warnings.push(crfPartialMessage(crfReady));
          }
          // CRF Instruction Guide embeds a rasterized image of each form's
          // rendering. When forms exist but some haven't been rendered, warn
          // softly (non-blocking) and name the affected forms so the user
          // doesn't have to hunt through the CRF Builder for them. Cap the
          // inline list at 5 names to keep the card readable.
          if (
            card.id === 'crfInstructionGuide'
            && hasApprovedForms
            && crfStats
            && crfStats.unrenderedForms.length > 0
          ) {
            const names = crfStats.unrenderedForms;
            const n = names.length;
            const listed = names.slice(0, 5).join(', ');
            const overflow = n > 5 ? ` and ${n - 5} more` : '';
            warnings.push(
              `${n} ${n === 1 ? 'form has' : 'forms have'} not been rendered yet: ` +
              `${listed}${overflow}. In the CRF Builder, use Generate CRF on ` +
              `${n === 1 ? 'it' : 'each'} (or Generate All CRFs) before generating the guide.`
            );
          }
          // Data Dictionary dependencies
          if (['sdtmMapping', 'edts'].includes(card.id) && documentStatuses.dataDictionary !== 'generated') {
            warnings.push('Recommended: generate Data Dictionary first for accurate variable mapping.');
          }
          if (warnings.length === 0 && infos.length === 0) return null;
          return (
            <>
              {warnings.map((w, i) => (
                <p key={`w${i}`} className="text-xs text-amber-600 mb-1">{w}</p>
              ))}
              {infos.map((m, i) => (
                <p key={`i${i}`} className="text-xs text-gray-600 mb-1">{m}</p>
              ))}
              <div className="mb-2" />
            </>
          );
        })()}

        {/* Last generation error — pinned to the card so a user who looked away
            still sees what went wrong and what to do (the toast is persistent
            but dismissible). Only the safe message + reference code are shown;
            no paths/internals. */}
        {card.id === 'crfInstructionGuide' && guideError && (
          <div className="mb-3 rounded-lg border border-red-200 bg-red-50 p-3">
            <div className="flex items-start gap-2">
              <AlertCircle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-xs font-medium text-red-800">Last guide generation didn’t finish</p>
                <p className="text-xs text-red-700 mt-0.5 break-words">{guideError.message}</p>
                {/* Named forms from a structured precondition payload
                    (missing or duplicate renderings). The list is already
                    normalized to display strings via formLabels(), which
                    handles both the job path's {formCode,formName} objects
                    and the sync path's plain strings. Cap at 8 for
                    readability. */}
                {guideError.forms && guideError.forms.length > 0 && (
                  <ul className="mt-1.5 list-disc list-inside space-y-0.5">
                    {guideError.forms.slice(0, 8).map((f, i) => (
                      <li key={i} className="text-xs text-red-700 break-words">{f}</li>
                    ))}
                    {guideError.forms.length > 8 && (
                      <li className="text-xs text-red-600 list-none">
                        …and {guideError.forms.length - 8} more
                      </li>
                    )}
                  </ul>
                )}
                {guideError.errorRef && (
                  <p className="text-[11px] text-red-500 mt-1">Reference: {guideError.errorRef}</p>
                )}
              </div>
              <button
                onClick={() => setGuideError(null)}
                className="text-red-400 hover:text-red-600 flex-shrink-0"
                title="Dismiss"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        )}

        {/* Background-job "may be stalled" note + Restart. Only while an
            active guide job's updatedAt hasn't advanced for ~15 min.
            Restart calls startCrfGuideJob again, which supersedes the old
            job server-side. */}
        {card.id === 'crfInstructionGuide' && guideJobStalled && guideJob &&
          (guideJob.status === 'rendering' || guideJob.status === 'finalizing') && (
          <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
            <div className="flex items-start gap-2">
              <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-xs font-medium text-amber-800">Generation may be stalled</p>
                <p className="text-xs text-amber-700 mt-0.5 break-words">
                  This run hasn’t progressed for a while. You can restart it — the previous run will be replaced.
                </p>
                {canGenerate && (
                  <button
                    onClick={handleStartGuideJob}
                    className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-amber-300 bg-white px-2.5 py-1 text-xs font-medium text-amber-800 hover:bg-amber-100"
                  >
                    <RefreshCw className="h-3.5 w-3.5" />
                    Restart
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Adoption sits OUTSIDE the readiness branch chain below, deliberately.
            The Data Dictionary card renders several different action blocks —
            generated, generating, blocked-on-prerequisites, plain Generate —
            and adoption is valid across all of them. Putting it inside any one
            branch hid it in exactly the case it exists for, a project whose
            packet-uploaded forms are still draft.

            THE BUTTON IS ALWAYS OFFERED, and asks the server nothing first.
            The page used to call a read-only check and disable the button until
            it answered; since adoption is never refused because documents
            already exist, that check could only say yes, while a cold start
            left the button dead for seconds and a failed call left it dead for
            good. The server is still the authority — the apply re-reads it. */}
        {card.id === 'dataDictionary' && (() => {
          const adopt = adoptButtonState();
          return (
            <button
              type="button"
              onClick={() => setShowAdoptDd(true)}
              title={adopt.title}
              className="w-full mb-2 flex items-center justify-center space-x-2 text-sm font-medium px-4 py-2 rounded-lg border border-gray-300 bg-white shadow-sm transition-all duration-150 text-gray-700 hover:bg-gray-50 hover:shadow"
            >
              <FileUp className="h-4 w-4" />
              <span>{adopt.label}</span>
            </button>
          );
        })()}

        {/* Action buttons. items-start: a slow download's timer line sits
            under its button without shifting the others. */}
        <div className="flex items-start space-x-2">
          {card.id === 'crfInstructionGuide' && !guideLoaded && status !== 'generating' ? (
            /* Initial load: show a neutral placeholder until the guide doc
               listener resolves, so the card doesn't flash "Generate" before
               flipping to "Download Guide". */
            <button
              disabled
              className="flex-1 btn-primary flex items-center justify-center space-x-2 text-sm font-medium opacity-60 cursor-default"
            >
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Loading…</span>
            </button>
          ) : status === 'generated' && card.id === 'dataDictionary' ? (
            /* Data Dictionary: Download (CDASH CSV, plus REDCap CSV for REDCap studies) + Apply Dictionary Edits + Regenerate */
            <>
              <CardDownloadButton projectId={projectId} group={card.id} className="flex-1" options={cardDownloadOptions(card.id)} />
              <button
                onClick={() => setShowApplyDdEdits(true)}
                className="flex-1 flex items-center justify-center space-x-2 text-sm font-medium px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 shadow-sm hover:shadow transition-all duration-150"
                title="Upload the data dictionary you exported from this study after editing it, and apply those edits to the forms and the dictionary. This is not the REDCap Import DD in the CRF Builder."
              >
                <Upload className="h-4 w-4" />
                <span>Apply Dictionary Edits</span>
              </button>
              <button
                onClick={() => handleGenerate(card.id)}
                disabled={generatingDocs.has(card.id) || !canGenerate || formsGate.blocked}
                className="px-3 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                title={!canGenerate ? "Your role doesn't allow regenerating documents." : (formsGate.reason ?? 'Regenerate')}
              >
                {generatingDocs.has(card.id) ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4 text-gray-500" />
                )}
              </button>
            </>
          ) : status === 'generated' && card.id === 'crfInstructionGuide' ? (
            /* CRF Instruction Guide: Download (Word) + Regenerate */
            <>
              <CardDownloadButton projectId={projectId} group={card.id} className="flex-1" options={cardDownloadOptions(card.id)} />
              <button
                onClick={() => handleGenerate(card.id)}
                disabled={generatingDocs.has(card.id) || !canGenerate || formsGate.blocked}
                className="px-3 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                title={!canGenerate ? "Your role doesn't allow regenerating documents." : (formsGate.reason ?? 'Regenerate')}
              >
                {generatingDocs.has(card.id) ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4 text-gray-500" />
                )}
              </button>
            </>
          ) : status === 'generated' ? (
            /* Other documents: View + Download + Regenerate */
            <>
              <button
                onClick={() => toggleDetail(card.id)}
                className={`flex-1 px-4 py-2 rounded-lg flex items-center justify-center space-x-2 text-sm font-medium transition-colors ${
                  isExpanded
                    ? 'bg-primary-50 text-primary-700 border border-primary-200'
                    : 'bg-gray-50 text-gray-700 border border-gray-200 hover:bg-gray-100'
                }`}
              >
                {isExpanded ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
                <span>{isExpanded ? 'Hide' : 'View'}</span>
              </button>
              <CardDownloadButton projectId={projectId} group={card.id} className="flex-1" options={cardDownloadOptions(card.id)} />
              <button
                onClick={() => handleGenerate(card.id)}
                disabled={generatingDocs.has(card.id) || !canGenerate || formsGate.blocked}
                className="px-3 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                title={!canGenerate ? "Your role doesn't allow regenerating documents." : (formsGate.reason ?? 'Regenerate')}
              >
                {generatingDocs.has(card.id) ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4 text-gray-500" />
                )}
              </button>
            </>
          ) : card.comingSoon ? (
            <button
              disabled
              className="w-full px-4 py-2 bg-gray-100 text-gray-400 rounded-lg cursor-not-allowed text-sm font-medium"
            >
              Coming Soon
            </button>
          ) : (card.id === 'crfInstructionGuide' && !hasApprovedForms) ? (
            /* No CRFs at all — block and route the user to the CRF Builder. */
            <div className="w-full space-y-2">
              {/* Same rule as the dependency warnings above — on 2026-08-28 this
                  branch told a customer their 38 forms did not exist when the
                  true state was "not approved". */}
              <p className="text-xs text-amber-600">{crfReadinessMessage(crfReady)}</p>
              <button
                onClick={() => navigate(`/project/${projectId}/crf-builder`)}
                className="w-full px-4 py-2 bg-gray-50 text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-100 text-sm font-medium"
              >
                Go to CRF Builder
              </button>
            </div>
          ) : (needsApprovedForms(card.id) && !hasApprovedForms) ? (
            <button
              disabled
              className="w-full px-4 py-2 bg-gray-100 text-gray-400 rounded-lg cursor-not-allowed text-sm font-medium"
            >
              {crfReady.state === 'unknown' ? 'Checking…' : crfReady.state === 'unapproved' ? 'Approve CRFs First' : 'Generate CRFs First'}
            </button>
          ) : card.id === 'crfInstructionGuide' && guideJob &&
              (guideJob.status === 'rendering' || guideJob.status === 'finalizing') ? (
            /* REAL background-job progress (formsDone / formsTotal). Takes
               precedence over the simulated stepper for the job path. */
            (() => {
              const total = guideJob.formsTotal || 0;
              const done = guideJob.formsDone || 0;
              const pct = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : (guideJob.status === 'finalizing' ? 95 : 5);
              const label = guideJob.status === 'finalizing'
                ? 'Finalizing…'
                : (total > 0 ? `Writing form instructions — ${done} of ${total}` : 'Writing form instructions…');
              const elapsedSec = guideJob.createdAtMs > 0
                ? Math.max(0, Math.floor((jobNowMs - guideJob.createdAtMs) / 1000))
                : 0;
              return (
                <div className="w-full space-y-2">
                  <div className="flex items-center space-x-2 text-sm text-primary-700">
                    <Loader2 className="h-4 w-4 animate-spin flex-shrink-0" />
                    <span className="truncate flex-1">{label}</span>
                    <span className="text-xs text-gray-400 tabular-nums flex-shrink-0">
                      {Math.floor(elapsedSec / 60)}:{String(elapsedSec % 60).padStart(2, '0')}
                    </span>
                    {total > 0 && (
                      <span className="text-xs text-gray-400 tabular-nums flex-shrink-0">{pct}%</span>
                    )}
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                    <div
                      className="h-full bg-primary-500 rounded-full transition-all duration-700 ease-out"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  {(guideJob.formsFailed ?? 0) > 0 && (
                    <p className="text-xs text-amber-600">
                      {guideJob.formsFailed} {guideJob.formsFailed === 1 ? 'form' : 'forms'} could not be rendered and will be skipped.
                    </p>
                  )}
                </div>
              );
            })()
          ) : status === 'generating' && generationProgress.docId === card.id ? (
            /* Simulated progress stepper (quick-generate / other docs). */
            <div className="w-full space-y-2">
              <div className="flex items-center space-x-2 text-sm text-primary-700">
                <Loader2 className="h-4 w-4 animate-spin flex-shrink-0" />
                <span className="truncate flex-1">{generationProgress.message}</span>
                <span className="text-xs text-gray-400 tabular-nums flex-shrink-0">
                  {Math.floor(elapsedSeconds / 60)}:{String(elapsedSeconds % 60).padStart(2, '0')}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                <div
                  className="h-full bg-primary-500 rounded-full transition-all duration-1000 ease-out"
                  style={{ width: `${generationProgress.percent}%` }}
                />
              </div>
            </div>
          ) : !canGenerate ? (
            /* Read-only members can't generate. Show a clear hint
               instead of a disabled CTA so they understand the
               document hasn't been built yet rather than just being
               denied. */
            <div className="w-full px-4 py-2 rounded-lg bg-slate-50 text-slate-600 text-sm font-medium text-center ring-1 ring-inset ring-slate-200">
              Not yet generated. Ask an Editor or Manager to create this document.
            </div>
          ) : card.id === 'crfInstructionGuide' ? (
            /* CRF Instruction Guide: primary background-job Generate, plus
               a secondary quick/draft action that runs synchronously. */
            <div className="w-full space-y-2">
              <button
                onClick={() => handleGenerate(card.id)}
                disabled={status === 'generating'}
                className="w-full btn-primary disabled:opacity-50 flex items-center justify-center space-x-2 text-sm font-medium"
              >
                {status === 'generating' ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Generating...</span>
                  </>
                ) : (
                  <>
                    <Play className="h-4 w-4" />
                    <span>Generate</span>
                  </>
                )}
              </button>
              <button
                onClick={handleQuickGenerateGuide}
                disabled={status === 'generating'}
                className="w-full px-4 py-2 rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed text-xs font-medium"
                title="Generate a quick draft now. Lower quality than the full Generate; runs while you wait."
              >
                Quick generate (faster, draft quality)
              </button>
            </div>
          ) : (
            <>
            <button
              onClick={() => handleGenerate(card.id)}
              disabled={status === 'generating'}
              className="w-full btn-primary disabled:opacity-50 flex items-center justify-center space-x-2 text-sm font-medium"
            >
              {status === 'generating' ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Generating...</span>
                </>
              ) : (
                <>
                  <Play className="h-4 w-4" />
                  <span>Generate</span>
                </>
              )}
            </button>
            </>
          )}
        </div>
        </div>{/* close p-6 wrapper */}

        {/* ============================================================ */}
        {/* Inline panel — renders INSIDE the card when expanded.         */}
        {/* Per the user's hard rule (memory: feedback_edc_card_inline),  */}
        {/* spec panels MUST render inside the card with a scrollable     */}
        {/* container, never as a separate block below the grid.          */}
        {/* ============================================================ */}
        {isExpanded && projectId && (
          <div className="border-t border-gray-200 bg-gray-50/30 animate-fade-in">
            <div className="px-5 py-2.5 flex items-center justify-between bg-white border-b border-gray-100">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                {card.title}
              </span>
              <button
                onClick={() => setExpandedDocId(null)}
                className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                title="Close panel"
              >
                <X className="h-4 w-4 text-gray-400" />
              </button>
            </div>
            <div className="max-h-[60vh] overflow-y-auto p-4">
              {card.id === 'dataManagementPlan' && (
                <DMPPanel projectId={projectId} generating={isGen('dataManagementPlan', dmpDoc)} />
              )}
              {card.id === 'editChecks' && editCheckSpec?.checkCount > 0 && (
                <EditCheckSpecPanel projectId={projectId} />
              )}
              {card.id === 'sdtmMapping' && (
                <SDTMMappingPanel projectId={projectId} generating={isGen('sdtmMapping', sdtmDoc)} />
              )}
              {card.id === 'edts' && (
                <EDTSPanel projectId={projectId} generating={isGen('edts', edtsDoc)} />
              )}
              {card.id === 'medicalCodingSpec' && (
                <MCSPanel projectId={projectId} generating={isGen('medicalCodingSpec', mcsDoc)} />
              )}
              {card.id === 'drcp' && (
                <DRCPPanel projectId={projectId} generating={isGen('drcp', drcpDoc)} />
              )}
              {card.id === 'saerp' && (
                <SAERPPanel projectId={projectId} generating={isGen('saerp', saerpDoc)} />
              )}
              {card.id === 'dblc' && (
                <DBLCPanel projectId={projectId} generating={isGen('dblc', dblcDoc)} />
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Render a non-inline card (linkout or placeholder). Visual structure mirrors
  // renderInlineCard exactly so all cards on Study Documents look uniform:
  // same outer container, same top accent bar, same p-6 padding, same h-10
  // icon, same title size, status badge top-right, description, action button
  // at the bottom. Behavior differs (linkout cards navigate, placeholder cards
  // are disabled) but the visual weight is identical to inline cards.
  const renderExtraCard = (extra: StudyDocumentExtra) => {
    const handleClick = () => {
      if (extra.behavior === 'linkout' && extra.route && projectId) {
        navigate(`/project/${projectId}/${extra.route}${extra.query ? `?${extra.query}` : ''}`);
      }
    };
    const isClickable = extra.behavior === 'linkout';
    // Strip "in " prefix from routeLabel for the action button text
    const buttonLabel = extra.routeLabel
      ? `Open ${extra.routeLabel.replace(/^in /i, '')}`
      : 'Open';

    // Per-study badge, derived from the real underlying artifact when this card
    // declares a dynamicSource. Static `status` is only used for 'planned'
    // (Coming Soon) cards now.
    // The card's Download, when its document can be downloaded today: the
    // consent form once it has content (as on Study Design), the visit/form
    // matrix always (the server refuses an unsaved matrix with its own line).
    const consentContent = (consentArtifact as { content?: unknown } | null)?.content;
    const downloadOptions: DownloadOption[] =
      !projectId
        ? []
        : extra.download === 'visitFormMatrix'
        ? studyDocumentDownloadOptions('visitFormMatrix', { projectId })
        : extra.download === 'consentForm' && consentContent
        ? [{
            label: 'Word',
            title: 'Download the Informed Consent Form as a Word document',
            request: consentDownloadRequest({
              content: consentContent,
              projectName,
              precheck: () => downloadBlockedReason(projectId, 'consent_form'),
            }),
          }]
        : [];
    const hasDownload = downloadOptions.length > 0;

    const derivedBadge: StudyDocBadge | null =
      extra.dynamicSource === 'consent'
        ? deriveConsentBadge(consentArtifact)
        : extra.dynamicSource === 'visitMatrix'
        ? deriveVisitMatrixBadge(visitMapping)
        : null;

    return (
      <div
        key={extra.id}
        title={extra.tooltip}
        className="bg-white rounded-xl border border-gray-100 hover:border-gray-200 shadow-card hover:shadow-card-hover transition-all duration-200 flex flex-col overflow-hidden"
      >
        {/* Top accent bar — blue once the document is Ready for this study. */}
        <div
          className={`h-1 ${derivedBadge === 'ready' ? 'bg-blue-500' : 'bg-transparent'}`}
        />

        <div className="p-6 flex flex-col flex-1">
          {/* Top: Icon + Title + Status */}
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center space-x-3">
              <div className={`h-10 w-10 ${extra.iconBgColor} rounded-xl flex items-center justify-center`}>
                <div className={extra.iconColor}>{extra.icon}</div>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">{extra.name}</h3>
              </div>
            </div>
            {derivedBadge === 'ready' && (
              <span className="badge-success">
                <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
                Ready
              </span>
            )}
            {derivedBadge === 'in_progress' && (
              <span className="inline-flex items-center text-[11px] font-medium text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded">
                In progress
              </span>
            )}
            {derivedBadge === 'not_started' && (
              <span className="inline-flex items-center text-[11px] font-medium text-gray-500 bg-gray-100 border border-gray-200 px-2 py-0.5 rounded">
                Not started
              </span>
            )}
            {!derivedBadge && extra.status === 'planned' && (
              <span className="badge-neutral">Coming Soon</span>
            )}
          </div>

          {/* Description */}
          <p className="text-sm text-gray-500 mb-4 flex-1">{extra.description}</p>

          {/* Action buttons: Open, and Download when the document can be downloaded */}
          <div className="flex items-start space-x-2">
            {isClickable ? (
              <button
                onClick={handleClick}
                className={`flex-1 ${hasDownload ? 'btn-secondary' : 'btn-primary'} flex items-center justify-center space-x-2 text-sm font-medium`}
              >
                <ExternalLink className="h-4 w-4" />
                <span>{buttonLabel}</span>
              </button>
            ) : (
              <button
                disabled
                className="flex-1 px-4 py-2 bg-gray-100 text-gray-400 rounded-lg cursor-not-allowed text-sm font-medium"
              >
                Coming Soon
              </button>
            )}
            {hasDownload && (
              <CardDownloadButton projectId={projectId} group={extra.download!} className="flex-1" options={downloadOptions} />
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 tracking-tight">
          Study Documents
        </h1>
        <p className="text-gray-600 mt-2 leading-relaxed max-w-3xl">
          Generate the documentation your sponsor, CRO, and sites need to run this trial.
          Cards in this hub either generate inline below or open in their canonical editor.
        </p>
        <div className="mt-3 text-xs text-gray-500">
          {DOCUMENT_CARDS.length + STUDY_DOCUMENTS_EXTRA.length} document types in this hub
        </div>
      </div>

      {/* Quality Audit panel REMOVED 2026-07-24 — the legacy audit system was
          retired; Verification (its own tab + nav chip) is the single checker. */}
      {/* Validation moved off this page 2026-07-23: it lives on its own tab, and
          its status now shows as an ambient chip in the project nav. This hub is
          for generating documents only. */}

      {/* ============================================================ */}
      {/* Study Documents — 5 grouped sections                          */}
      {/* ============================================================ */}
      {STUDY_GROUP_ORDER.map((groupKey) => {
        const inlineInGroup = DOCUMENT_CARDS.filter((c) => INLINE_CARD_GROUPS[c.id] === groupKey);
        const extraInGroup = STUDY_DOCUMENTS_EXTRA.filter((d) => d.group === groupKey);
        if (inlineInGroup.length === 0 && extraInGroup.length === 0) return null;

        return (
          <div key={groupKey} className="space-y-3">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mt-4 mb-1">
              {STUDY_GROUP_LABELS[groupKey]}
            </h3>
            <div className="space-y-3">
              {inlineInGroup.map(renderInlineCard)}
              {extraInGroup.map(renderExtraCard)}
            </div>
          </div>
        );
      })}

      {showAdoptDd && projectId && (
        <ApplyDictionaryEditsModal
          projectId={projectId}
          mode="adopt"
          onClose={() => setShowAdoptDd(false)}
          onApplied={() => { /* keep open: the rename list is the point */ }}
        />
      )}
      {showApplyDdEdits && projectId && (
        <ApplyDictionaryEditsModal
          projectId={projectId}
          onClose={() => setShowApplyDdEdits(false)}
          // NOT a close. The modal's done stage is where the stale-downstream
          // report is shown, and closing on success threw it away before the
          // user could read which documents the edit had just invalidated.
          onApplied={() => { /* keep open: the result is the point */ }}
        />
      )}
    </div>
  );
}

